import { useEditorStore } from '@/store/use-editor-store';
import { useRuntimeStore } from '@/store/use-runtime-store';
import { useDataSource } from './use-data-source';
import { useLayerStore } from '@/store/use-layer-store';
import { useImagesStore } from '@/store/use-images-store';
import { useTranslationStore } from '@/store/use-translation-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Shape } from '@/services/shape';
import { onBeforeUnmount, onDeactivated, ref } from 'vue';
import { LayerUtils } from '@/utils/shape/layer.util';
import { WatchfaceService } from '@/services/watchface';
import { EventType, emitter } from '@/utils/emitter.utils';
import {
  useImageBufferCache,
  useWatchfaceDevices,
  useWatchfacePowerClass,
  useWatchfaceRam,
  useWathchfaceType
} from '.';
import { GlobalConfig } from '@/services/gloabl.config';
import { WatchfaceRenderer } from '@/watch-renderer';
import { useFormatsStore } from '@/store/use-formats-store';
import { canUse } from '@/utils/can-use.utils';
import { getWatchfacePrivateId } from '@/utils/watchface-id.utils';
import { useDataSourceStore } from '@/store/use-data-source-store';

/**
 * 表盘正在打开状态
 */
export const watchfaceOpening = ref(false);
/**
 * 表盘正在加载图片资源中
 */
export const watchfaceImageLoading = ref(false);

export const useWatchfaceCorrectData = () => {
  const layerStore = useLayerStore();
  const { wholeDevices } = useWatchfaceDevices();
  const watchfaceStore = useWatchfaceStore();
  const { correctWathfaceType } = useWathchfaceType();

  const correctWatchfaceStore = () => {
    // 重置deviceType
    const deviceType = watchfaceStore.deviceType;
    const watchfaceDevice = wholeDevices.value.find(
      (item) =>
        item.deviceType == deviceType ||
        item.deviceTypeAlias?.includes(deviceType)
    );
    if (
      watchfaceDevice?.deviceType != deviceType &&
      watchfaceDevice?.deviceTypeAlias?.includes(deviceType)
    ) {
      watchfaceStore.description.deviceType = watchfaceDevice?.deviceType;
    }

    // 如果不允许使用压缩, 则将压缩默认成为false
    if (!canUse('compress').valid) {
      watchfaceStore.description.imageCompression = false;
    } else if (!GlobalConfig.adjustWatchfaceCompression) {
      // 如果不需要选择启用压缩，则默认设置为true
      watchfaceStore.description.imageCompression = true;
    }

    // 添加内部使用的_id
    if (!watchfaceStore.description._id) {
      watchfaceStore.description._id = getWatchfacePrivateId(
        watchfaceStore.deviceType
      );
    }
    // 处理watchface type
    watchfaceStore.description.watchfaceType = correctWathfaceType(
      watchfaceStore.description.deviceType ?? 'N62S',
      watchfaceStore.description.watchfaceType
    );
  };

  const correctLayerStore = () => {
    // 预处理themes
    let themes = LayerUtils.correctThemeLayersByWatchfaceType(
      layerStore.themes ?? [],
      watchfaceStore.watchfaceType
    );
    themes = LayerUtils.preHandleThemes(themes ?? []);

    themes.forEach((theme) =>
      LayerUtils.preHandleLayers(
        theme,
        watchfaceStore.deviceType,
        watchfaceStore.description
      )
    );

    layerStore.sync(themes);
  };

  return {
    correctWatchfaceStore,
    correctLayerStore
  };
};

export const useWatchfaceInit = () => {
  const editorStore = useEditorStore();
  const runtimeStore = useRuntimeStore();
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();
  const translationStore = useTranslationStore();
  const watchfaceStore = useWatchfaceStore();

  const { reset: resetDataSource } = useDataSource();
  const formatStore = useFormatsStore();
  const dataSourceStore = useDataSourceStore();
  const { themeRamItems, clear: clearRamCache } = useWatchfaceRam();
  const { initCachedImageSetInDB } = useImageBufferCache();
  const { clear: clearPowerData } = useWatchfacePowerClass();
  const { correctWatchfaceStore, correctLayerStore } =
    useWatchfaceCorrectData();

  const clearPreviousData = () => {
    // 清理旧数据
    watchfaceStore.$reset();
    imageStore.$reset();
    layerStore.$reset();
    editorStore.$reset();
    runtimeStore.$reset();
    translationStore.$reset();
    formatStore.$reset();
    Shape.clearCache();
    // resetDataSource(false);
    clearRamCache();
    themeRamItems.value = [];
    WatchfaceRenderer.clearCache();
    clearPowerData();
  };

  /**
   * 初始化表盘数据
   */
  const initWatchfaceData = async (path: string) => {
    try {
      console.time('INIT_WATCHFACE base data loaded');
      watchfaceOpening.value = true;
      // 清理旧数据
      clearPreviousData();

      watchfaceStore.path = path;

      let editorData: StoreEditorConfig | undefined;
      const readEditorData = async () => {
        editorData = await new WatchfaceService().editorConfig.getEditorData();
      };
      await Promise.all([
        watchfaceStore.sync(),
        readEditorData(),
        // 从idb中读取缓存数据
        initCachedImageSetInDB()
      ]);

      // 处理description
      correctWatchfaceStore();

      if (editorData) {
        // 预处理themes（含 aodDisplayMode）
        layerStore.sync(editorData);
        correctLayerStore();

        // 处理多语言
        if (editorData.i18n && editorData.i18n.translations['watchfaceName']) {
          editorData.i18n.translations = {
            watchfaceName: editorData.i18n.translations['watchfaceName']
          };
        }

        watchfaceStore.syncSlotFollowing(editorData.isSlotFollowing);
        translationStore.sync(editorData?.i18n);
        formatStore.sync(editorData.formats ?? {});
        dataSourceStore.sync(editorData.dataSource ?? {});
        // 获取editor后重新同步数据源默认值
        resetDataSource(false);
      }

      // 预先获取所有的shape
      layerStore.themes.forEach((theme) => {
        theme.children.forEach((layer) => {
          const stask = [layer];
          while (stask.length) {
            const layer = stask.shift();
            if (layer?.children) stask.push(...layer.children);
            if (!layer) continue;
            Shape.getShape(layer.id, layer);
          }
        });
      });

      // 为提高性能，不使用await
      watchfaceImageLoading.value = true;
      console.time('INIT_WATCHFACE images loaded');
      imageStore.sync().finally(() => {
        watchfaceImageLoading.value = false;
        console.timeEnd('INIT_WATCHFACE images loaded');
        emitter.emit(EventType['watchface:opened-resources'], path);
      });
      return true;
    } catch (error) {
      console.log('OPEN_NEW_WATCHFACE ERROR', error);
      return false;
    } finally {
      console.timeEnd('INIT_WATCHFACE base data loaded');
      watchfaceOpening.value = false;
      emitter.emit(EventType['watchface:opened'], path);
    }
  };

  onBeforeUnmount(() => {
    clearPreviousData();
  });
  onDeactivated(() => {
    clearPreviousData();
  });

  return {
    initWatchfaceData
  };
};
